import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, filter, map, switchMap, tap } from 'rxjs';

import { NgCommService } from '../../ajs-ng-communication.service';
import { AjsScopeService } from '../../ajs-scope.service';
import { AjsStateService } from '../../ajs-state.service';
import { DcMarketPlaceRequester, PermissionRequester } from '../../requesters';
import { CacheInspectorPopupComponent } from '../../ui/components/cache-inspector-popup/cache-inspector-popup.component';
import { AppAdapter } from '../adapters/app.adapter';
import { states } from '../application.states';
import {
	fetchAvailableMarketplaceMembersFail,
	fetchAvailableMarketplaceMembersSuccess,
	fetchKnownPermissionsPerElementFail,
	fetchKnownPermissionsPerElementSuccess,
	getAvailableMarketplaceMembers,
	goToGdHome,
	goToHdHome,
	goToProjects,
	openCacheInspector,
	setUser,
} from './app.actions';

@Injectable()
export class AppEffects {
	public redirectHome$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType('redirect:home'),
				tap(() =>
					this.ngCommService.notifyOnWarning(
						$localize`:i18n=@@general.project.invalid:`
					)
				)
			),
		{
			dispatch: false,
		}
	);

	public hdHomePage$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(goToHdHome),
				tap(() => this.stateService.go(states.HdHome))
			),
		{
			dispatch: false,
		}
	);

	public gdHomePage$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(goToGdHome),
				tap(() => this.stateService.go(states.GdHome))
			),
		{
			dispatch: false,
		}
	);

	public projectsPage$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(goToProjects),
				tap(() => this.stateService.go(states.Projects))
			),
		{
			dispatch: false,
		}
	);

	public openCacheInspector$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(openCacheInspector),
				tap(() =>
					this.modalService.openModal(CacheInspectorPopupComponent, {
						// Le composant affiche deja "CACHE INSPECTOR" : l'entete dit
						// de quel cache il s'agit, elle ne le repete pas.
						title: 'HTTP cache',
						subTitle: '',
					})
				)
			),
		{
			dispatch: false,
		}
	);

	// ##########################################################
	// Business Effects
	// ##########################################################

	// TODO: translate title keys
	public setTitleOnTransitionSuccess$ = createEffect(
		() =>
			this.ajsScopeService
				.listenOnRootScope<string>('state:transition:success')
				.pipe(tap((v) => this.titleService.setTitle(`Datachain - ${v}`))),
		{
			dispatch: false,
		}
	);

	public getKnownPermissionsPerElement$ = createEffect(() =>
		this.actions$.pipe(
			ofType(setUser),
			switchMap(() => this.permissionRequester.getKnownPermissions()),
			map((result) =>
				fetchKnownPermissionsPerElementSuccess({
					permissions: this.appAdapter.generatePermissions(result),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchKnownPermissionsPerElementFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public getAvailableMarketplaceUserMembers$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getAvailableMarketplaceMembers),
			filter((action) => action.consumerType === 'user'),
			switchMap((action) =>
				this.dcMarketPlaceRequester.getMarketPlaceUserMembers(
					action.page,
					action.searchTerm,
					action.filterFields,
					action.sortFields,
					'ATTRIBUTES',
					false
				)
			),
			map(({ elements, total }) =>
				fetchAvailableMarketplaceMembersSuccess({
					members: this.appAdapter.generateUserMembers(elements),
					total,
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchAvailableMarketplaceMembersFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public getAvailableMarketplaceGroupMembers$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getAvailableMarketplaceMembers),
			filter((action) => action.consumerType === 'group'),
			switchMap((action) =>
				this.dcMarketPlaceRequester.getMarketPlaceGroupMembers(
					action.page,
					action.searchTerm,
					action.filterFields,
					action.sortFields,
					'ATTRIBUTES',
					false
				)
			),
			map(({ elements, total }) =>
				fetchAvailableMarketplaceMembersSuccess({
					members: this.appAdapter.generateGroupMembers(elements),
					total,
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchAvailableMarketplaceMembersFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public constructor(
		private readonly actions$: Actions,
		private readonly titleService: Title,
		private readonly ajsScopeService: AjsScopeService,
		private readonly stateService: AjsStateService,
		private readonly ngCommService: NgCommService,
		private readonly modalService: AppModalService,
		private readonly store: Store,
		private readonly appAdapter: AppAdapter,
		private readonly permissionRequester: PermissionRequester,
		private readonly dcMarketPlaceRequester: DcMarketPlaceRequester
	) {}
}
